import { FC, Fragment, useState } from 'react';
import { useTheme, Box } from '@mui/system';
import { Trans, useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';

import { LangSwitcherView } from 'src/components/lang-switcher-view';
import { LanguagesCode } from 'src/i18n/constants';
import { ReactComponent as VSLogo } from 'src/assets/svg/vita-logo.svg';
import { ReactComponent as Line } from 'src/assets/svg/line.svg';
import { ReactComponent as HoSLogoWhite } from 'src/assets/svg/hos-logo-white.svg';
import { ReactComponent as HoSLogoBlack } from 'src/assets/svg/hos-logo-black.svg';

import { AuthMode } from '../enum/AuthMode';
import { OtpStep } from '../enum/OtpStep';

import { SupabaseOtpForm } from './supabase-otp';
import { SupabasePasswordForm } from './supabase-password-form';
import { SupabaseForgotPasswordForm } from './supabase-forgot-password-form';
import { styles } from './styles/otp-form';

export const SupabaseLoginForm: FC = () => {
    const {
        i18n: { language, changeLanguage },
    } = useTranslation();
    const [loginMode, setLoginMode] = useState<AuthMode>(AuthMode.OTP);
    const [otpStep, setOtpStep] = useState<OtpStep>(OtpStep.EMAIL);
    const [email, setEmail] = useState('');
    const { breakpoints } = useTheme();
    const isSmallScreen = useMediaQuery('(max-width:1023px)');

    const handleChange = (nextLang: string) => {
        changeLanguage(nextLang);
    };

    return (
        <Fragment>
            <LangSwitcherView
                language={language}
                options={[
                    {
                        value: LanguagesCode.English,
                        label: 'English',
                        code: 'gb',
                    },
                    {
                        value: LanguagesCode.Spanish,
                        label: 'Español',
                        code: 'es',
                    },
                ]}
                onChange={handleChange}
                css={{
                    position: 'absolute',
                    bottom: '24px',

                    [breakpoints.up('md')]: { bottom: 'unset', top: '30px', right: '40px' },
                }}
            />
            <Box css={styles.wrapper}>
                <Box css={styles.preview}>
                    <Box css={styles.previewContainer}>
                        <h1 css={styles.welcomeTitle}>
                            <Trans i18nKey="supabase_welcome" />
                        </h1>

                        <h5 css={styles.subtitle}>
                            <Trans i18nKey="supabase_login_to_my_account" />
                        </h5>
                    </Box>
                    <Box css={styles.logos}>
                        <VSLogo width={48} height={48} />
                        <Line css={styles.line} />
                        <HoSLogoWhite />
                    </Box>
                </Box>
                {isSmallScreen && (
                    <Box css={styles.logos}>
                        <VSLogo width={48} height={48} />
                        <Line css={styles.line} />
                        <HoSLogoBlack />
                    </Box>
                )}
                {loginMode === AuthMode.PASSWORD && (
                    <SupabasePasswordForm
                        email={email}
                        onEmailChange={(e) => setEmail(e.target.value)}
                        switchToOtp={() => setLoginMode(AuthMode.OTP)}
                        switchToForgotPassword={() => setLoginMode(AuthMode.FORGOT_PASSWORD)}
                    />
                )}
                {loginMode === AuthMode.OTP && (
                    <SupabaseOtpForm
                        step={otpStep}
                        goToNextStep={() => setOtpStep(OtpStep.TOKEN)}
                        goToPreviousStep={() => setOtpStep(OtpStep.EMAIL)}
                        email={email}
                        onEmailChange={(e) => setEmail(e.target.value)}
                        switchToPassword={() => setLoginMode(AuthMode.PASSWORD)}
                    />
                )}
                {loginMode === AuthMode.FORGOT_PASSWORD && (
                    <SupabaseForgotPasswordForm
                        email={email}
                        onEmailChange={(e) => setEmail(e.target.value)}
                        switchToPassword={() => setLoginMode(AuthMode.PASSWORD)}
                    />
                )}
            </Box>
        </Fragment>
    );
};
